/**
 * 将数组构建成树
 * @param {object} options
 *   @param {array} nodes 数据源
 *   @param {string} idKey 节点 id 的 key
 *   @param {string} pidKey 节点 id 的 key
 *   @param {string} childrenKey 子节点数组的 key
 *   @param {string|undefined|null|number} rootId 根节点的 id（一级节点的 pid）
 *   @param {object} formatKeys 需要做格式化的数据，{ label: '{name}', show: true, disabled: fn(node) }
 *    第一个增加 label 属性，value 为 node 节点的 name 属性的 value
 *    第二个增加 show 属性，value 为 true
 *    第三个增加 disabled 属性，value 为经过 fn(node) 计算的结果
 * @returns {array} tree
 */
export function treeBuild({
  nodes = [],
  idKey = 'id',
  pidKey = 'pid',
  childrenKey = 'children',
  rootId = '',
  formatKeys = {},
}) {
  const nodeKeyMap = {}; // 将数组转成 map
  const nodeKeyChildren = {}; // 以 pid 为key，value 为子节点的数组

  nodes.forEach((node) => {
    node = { ...node };

    // 额外属性操作
    Object.keys(formatKeys).forEach((key) => {
      if (typeof formatKeys[key] === 'string' && /^\{[a-z0-9_-]+\}$/i.test(formatKeys[key])) {
        const toKey = formatKeys[key].replace(/^(\{)([a-z0-9_-]+)(\})$/i, ($0, $1, $2) => $2);
        node[key] = node[toKey];
      } else if (typeof formatKeys[key] === 'function') {
        node[key] = formatKeys[key](node);
      } else {
        node[key] = formatKeys[key];
      }
    });

    nodeKeyMap[node[idKey]] = node;
    if (!nodeKeyChildren[node[pidKey]]) {
      nodeKeyChildren[node[pidKey]] = [];
    }

    nodeKeyChildren[node[pidKey]].push(node);
  });

  // 利用引用类型的地址，进行子节点属性挂载
  Object.keys(nodeKeyMap).forEach((key) => {
    if (nodeKeyChildren[key]) {
      nodeKeyMap[key][childrenKey] = (nodeKeyMap[key][childrenKey] || []).concat(nodeKeyChildren[key]);
    }
  });

  // 返回 rootId 数值
  return nodeKeyChildren[rootId] || [];
}

// 根据 node 节点数组，获取 id 的父级节点 id，返回整个树的节点值数组
export function getAncestorIds({
  id,
  nodes,
  idKey = 'id',
  pidKey = 'pid',
  rootId = '',
}) {
  const nodeKeyMap = {};
  const ancestorIds = [id];

  nodes.forEach((node) => {
    nodeKeyMap[node[idKey]] = node;
  });

  let currentId = id;
  while (nodeKeyMap[currentId] && nodeKeyMap[currentId][pidKey] && nodeKeyMap[currentId][pidKey] !== rootId) {
    currentId = nodeKeyMap[currentId][pidKey];
    ancestorIds.unshift(currentId);
  }

  return ancestorIds;
}

// 根据 node 树，获取 id 的父级节点 id
export function getAncstorIdsWithTree({
  id,
  nodeTree,
  idKey = 'id',
}) {
  // 递归查找节点 id 为 [id] 值，并将其父元素也塞进数组前面
  function deepFind(ancestorIds, arr) {
    let flag = false;
    for (let i = 0, len = arr.length; i < len; i++) {
      if (arr[i][idKey] === id || (arr[i].children && arr[i].children.length > 0 && deepFind(ancestorIds, arr[i].children))) {
        flag = true;
        ancestorIds.unshift(arr[i][idKey]);
        break;
      }
    }

    return flag;
  }
  const ancestorIds = [];
  deepFind(ancestorIds, nodeTree);

  return ancestorIds;
}
